import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {Pool} from '../../../../../models/landing-page/pool';
import {Paginator} from '../../../../../tools/pagination/paginator';
import {TableSorter} from '../../../../../tools/table-sorter/table-sorter';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {PoolService} from '../../../../../services/landing-page/pool.service';
import {PageTypeService} from '../../../../../services/page-type/page-type.service';
import {debounceTime, forkJoin, Subject} from 'rxjs';
import {PageType} from '../../../../../models/page-type';
import {ConfirmDirective} from '../../../../../directives/confirm/confirm.directive';
import {IsGrantedDirective} from '../../../../../directives/is-granted/is-granted.directive';
import {NgForOf, NgIf} from '@angular/common';
import {PaginatorComponent} from '../../../../layout/paginator/paginator.component';
import {TableSorterHeaderDirective} from '../../../../../directives/table-sorter/table-sorter-header.directive';
import {RouterLink} from '@angular/router';
import {IsGranted} from '../../../../../decorator/is-granted';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {TableSorterDirective} from '../../../../../directives/table-sorter/table-sorter.directive';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {DropdownSelectComponent} from '../../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {ListFilters} from '../../../../../tools/list-filters/list-filters';
import {ListFilterService} from '../../../../../services/list-filter/list-filter.service';
import {PoolMode} from '../../../../../constants/pool-mode';

@Component({
  selector: 'app-landing-page-pool-list',
  imports: [
    ConfirmDirective,
    IsGrantedDirective,
    NgForOf,
    NgIf,
    PaginatorComponent,
    TableSorterHeaderDirective,
    RouterLink,
    TableSorterDirective,
    ReactiveFormsModule,
    FormsModule,
    DropdownSelectComponent,
    DropdownSelectOptionComponent
  ],
  templateUrl: './landing-page-pool-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './landing-page-pool-list.component.scss'
})
@IsGranted(['LANDING_PAGE_VIEW'])
@MetaTitle('Pool management')
export class LandingPagePoolListComponent implements OnInit {
  pageTypes: PageType[] = [];
  pools: Pool[] = [];

  filters: ListFilters;
  nameChange$ = new Subject<void>();
  identifierChange$ = new Subject<void>();

  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('id', 'asc');

  constructor(
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private pageTypeService: PageTypeService,
    private poolService: PoolService,
    private listFilterService: ListFilterService,
  ) {
    this.filters = listFilterService.createFilters({
      name: 'string',
      identifier: 'string',
      typeId: 'number[]',
    }, this.paginator, this.tableSorter);
  }

  ngOnInit(): void {
    this.loader.startLoading();

    forkJoin([
      this.pageTypeService.findAll({sort: 'name'}),
    ]).subscribe(([pageTypes]) => {
      this.pageTypes = pageTypes;

      this.loader.stopLoading();
    })

    this.nameChange$.pipe(debounceTime(1000)).subscribe(() => this.filters.onFilterChange());
    this.identifierChange$.pipe(debounceTime(1000)).subscribe(() => this.filters.onFilterChange());
    this.filters.init(() => this.fetchAll());
  }

  refresh(): void {
    this.fetchAll();
  }

  fetchAll(): void {
    this.loader.startLoading();
    this.poolService.findAll(this.filters.toParams(), this.paginator, this.tableSorter).subscribe((pools: Pool[]) => {
      this.pools = pools;
      this.loader.stopLoading();
    });
  }

  delete(pool: Pool): void {
    if (pool.id) {
      this.loader.startLoading();
      this.poolService.delete(pool.id).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Pool has been deleted');
        this.refresh();
      });
    }
  }

  onNameChange(): void {
    this.nameChange$.next();
  }

  onIdentifierChange(): void {
    this.identifierChange$.next();
  }

  getModeText(pool: Pool): string {
    switch (pool.mode) {
      case PoolMode.RANDOM:
        return 'Random';
      case PoolMode.ORDERED_CAPPING:
        return 'Ordered Capping (' + pool.cappingCount + ' / ' + pool.cappingDuration + ' min)';
    }

    return '';
  }
}
